import { useEffect, useLayoutEffect, useRef } from 'react';
import styled from 'styled-components';

const AdReserve = styled.div`
  position: relative;
`;

const AdIframe = styled.iframe`
  display: block;
  border: none;
  position: absolute;
  left: 0;
  top: 0;
  transform-origin: 0 0;
`;

export interface AdFrameProps {
  src: string;
  width: number;
  height: number;
  scale: number;
  iframeName?: string;

  /**
   * Called when the frame asks for its dynamic data, not when this renders.
   *
   * A function rather than the payload because both callers build theirs by
   * deep-copying a property tree, and both render on every property edit.
   * Called on demand, that cost is paid once per request; computed at render
   * time it would be paid per render, and the payload could also go stale
   * between the render that built it and the request that wanted it.
   */
  getDynamicData: () => unknown;
}

/**
 * The scaled ad iframe, shared by the Creative and Tearsheet AdViewFrames.
 *
 * Everything either of them does to decide *whether* to show an ad, and what
 * to show instead, stays with them; this is only the showing. It reads no
 * store, so both callers reach it entirely through props.
 */
function AdFrame({
  src,
  width,
  height,
  scale,
  iframeName,
  getDynamicData,
}: AdFrameProps) {
  const adIframeRef = useRef<HTMLIFrameElement>(null);

  // Held in a ref so the listener below can subscribe once and stay. Callers
  // pass an inline arrow, so an effect depending on the prop directly would
  // tear the listener down and rebuild it on every render.
  //
  // Assigned in a layout effect rather than during render: a render can be
  // discarded, and nothing here needs the new value before the commit - the
  // message it serves cannot arrive until the frame is in the document.
  const getDynamicDataRef = useRef(getDynamicData);
  useLayoutEffect(() => {
    getDynamicDataRef.current = getDynamicData;
  }, [getDynamicData]);

  useEffect(() => {
    const onWindowMessage = (event: MessageEvent) => {
      // No check that the message came from this frame, and the reply goes to
      // targetOrigin '*'. Both are carried over unchanged from the two
      // components this was extracted from, deliberately - tightening either
      // is a behavior change against template bundles that are user-uploaded
      // ZIPs we cannot audit.
      const frameWindow = adIframeRef.current?.contentWindow;
      if (event.data !== 'requestDynamicData' || !frameWindow) return;
      frameWindow.postMessage(getDynamicDataRef.current(), '*');
    };

    window.addEventListener('message', onWindowMessage);
    return () => {
      window.removeEventListener('message', onWindowMessage);
    };
  }, []);

  return (
    // Reserves exactly the space the scaled ad occupies, and carries no
    // transform of its own. It used to be sized width*scale AND scaled, so it
    // painted at width*scale*scale - which at scale > 1 left
    // width*scale*(scale-1) of empty scrollable area past the ad, and a
    // scrollbar in the band where the ad itself still fitted.
    <AdReserve
      style={{
        width: `${width * scale}px`,
        height: `${height * scale}px`,
      }}
    >
      <AdIframe
        ref={adIframeRef}
        // React omits an attribute whose value is undefined, so an absent
        // iframeName leaves the frame unnamed rather than naming it ''.
        name={iframeName}
        src={src}
        // Scaled here rather than on an ancestor, which needs no extra
        // element: the iframe was already inside a transformed subtree, so
        // this changes nothing about how its content is rasterized.
        //
        // Absolutely positioned so its unscaled width x height layout box
        // cannot inflate the wrapper - below 100% that box is larger than the
        // space the ad actually needs.
        style={{
          width: `${width}px`,
          height: `${height}px`,
          transform: `scale(${scale})`,
        }}
      />
    </AdReserve>
  );
}

export default AdFrame;
